import type { LiveBreakdown, LiveBreakdownPart } from "../../types/generated/live";
import { interpolate, type Labels } from "./labels";

// What a breakdown is worded with: the encounter card carries only these.
export type BreakdownLabels = Pick<Labels, "parts" | "stats" | "sum">;

// The movement parts name their own box and carry the operator in the sum, so they have no label of their own.
const BARE = new Set(["current", "run_multiplier", "climb_penalty", "swim_divisor"]);

export function partLabel(part: LiveBreakdownPart, labels: BreakdownLabels): string {
	if (BARE.has(part.key)) return "";
	const params: Record<string, string> = { ...part.params };
	if (params.stat) params.stat = labels.stats[params.stat] ?? params.stat.slice(0, 3).toUpperCase();
	return interpolate(labels.parts[part.key] ?? part.key, params);
}

function raw(breakdown: LiveBreakdown): number {
	const values = breakdown.parts.map((part) => part.value);
	if (values.length === 0) return breakdown.total;
	switch (breakdown.rule) {
		case "max":
			return Math.max(...values);
		case "product":
			return values.reduce((a, b) => a * b, 1);
		case "quotient":
			return Math.floor(values.slice(1).reduce((a, b) => a / b, values[0]));
		default:
			return values.reduce((a, b) => a + b, 0);
	}
}

function bounds(breakdown: LiveBreakdown, labels: BreakdownLabels): string {
	const value = raw(breakdown);
	if (breakdown.at_least != null && value < breakdown.at_least) return ` (${interpolate(labels.sum.at_least, { n: breakdown.at_least })})`;
	if (breakdown.at_most != null && value > breakdown.at_most) return ` (${interpolate(labels.sum.at_most, { n: breakdown.at_most })})`;
	return "";
}

function joined(items: string[], and: string): string {
	return items.length > 1 ? `${items.slice(0, -1).join(", ")} ${and} ${items[items.length - 1]}` : (items[0] ?? "");
}

// The sum a popover shows, parts labelled in parentheses: "10 (Base) + 2 (MOD. DES) = 12", "6 − 2 = 4", "6 × 2 = 12",
// "maior entre JPC 8 e JPS 10 = 10". A zero part after the first is left out, and a floor or ceiling that held is said.
export function sumLine(breakdown: LiveBreakdown, labels: BreakdownLabels): string {
	const { parts, rule } = breakdown;
	if (parts.length === 0) return "";
	let body: string;
	if (rule === "max") {
		body = interpolate(labels.sum.highest, {
			parts: joined(
				parts.map((part) => `${partLabel(part, labels)} ${part.value}`),
				labels.sum.and,
			),
		});
	} else {
		const operator = rule === "product" ? "×" : rule === "quotient" ? "÷" : null;
		body = parts
			.filter((part, index) => index === 0 || part.value !== 0)
			.map((part, index) => {
				const label = partLabel(part, labels);
				const text = `${Math.abs(part.value)}${label ? ` (${label})` : ""}`;
				return index === 0 ? `${part.value < 0 ? "−" : ""}${text}` : `${operator ?? (part.value < 0 ? "−" : "+")} ${text}`;
			})
			.join(" ");
	}
	return `${body} = ${breakdown.total}${bounds(breakdown, labels)}`;
}

const SCORE_STAT: Record<string, string> = { dex_score: "destreza", wis_score: "sabedoria" };

// The abbreviation of the score that won the initiative (DES or SAB); null when nothing was compared.
export function initiativeStat(breakdown: LiveBreakdown, labels: BreakdownLabels): string | null {
	const winner = breakdown.parts.find((part) => part.chosen) ?? breakdown.parts[0];
	const stat = winner && SCORE_STAT[winner.key];
	return (stat && labels.stats[stat]) || null;
}
